import { FC, useId, useMemo } from "react";

export interface LineSeries {
	label: string;
	color: string; // tailwind text-* or hex; we use it as `stroke`
	data: { x: string; y: number | null }[];
	faded?: boolean;
}

interface Props {
	series: LineSeries[];
	ariaLabel: string;
	height?: number;
	yLabel?: string;
}

const formatTickValue = (v: number): string => {
	if (v >= 1_000_000)
		return `${(v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1)}M`;
	if (v >= 1_000) return `${(v / 1_000).toFixed(v % 1_000 === 0 ? 0 : 1)}k`;
	return v % 1 === 0 ? v.toFixed(0) : v.toFixed(1);
};

const formatXLabel = (raw: string): string => {
	if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
		const d = new Date(`${raw}T00:00:00Z`);
		if (!isNaN(d.getTime()))
			return d.toLocaleDateString("en-GB", {
				day: "numeric",
				month: "short",
				timeZone: "UTC"
			});
	}
	if (/^\d{4}-\d{2}$/.test(raw)) {
		const d = new Date(`${raw}-01T00:00:00Z`);
		if (!isNaN(d.getTime()))
			return d.toLocaleDateString("en-GB", {
				month: "short",
				year: "2-digit",
				timeZone: "UTC"
			});
	}
	if (raw.includes("T")) {
		const d = new Date(raw);
		if (!isNaN(d.getTime()))
			return d.toLocaleDateString("en-GB", {
				day: "numeric",
				month: "short",
				timeZone: "UTC"
			});
	}
	return raw.length > 10 ? `${raw.slice(0, 10)}…` : raw;
};

const pickXTickIndices = (count: number, maxTicks: number): number[] => {
	if (count <= maxTicks) return Array.from({ length: count }, (_, i) => i);
	const indices: number[] = [0];
	const step = (count - 1) / (maxTicks - 1);
	for (let i = 1; i < maxTicks - 1; i++) indices.push(Math.round(step * i));
	indices.push(count - 1);
	return [...new Set(indices)];
};

const gridLines = [0, 0.25, 0.5, 0.75, 1];

const LineChart: FC<Props> = ({ series, ariaLabel, height = 220, yLabel }) => {
	const id = useId();
	const allValues = series.flatMap((s) =>
		s.data.map((d) => d.y).filter((v): v is number => v !== null)
	);
	const hasData = series.length > 0 && allValues.length > 0;

	const max = hasData ? Math.max(...allValues) : 0;
	const min = hasData ? Math.min(0, ...allValues) : 0;
	const range = max - min || 1;

	const xRef = hasData
		? (series.find((s) => s.data.length > 0)?.data.map((d) => d.x) ?? [])
		: [];
	const xCount = xRef.length;

	const yTicks = useMemo(
		() => gridLines.map((g) => ({ frac: g, value: min + (1 - g) * range })),
		[min, range]
	);

	const xTickIndices = useMemo(() => pickXTickIndices(xCount, 6), [xCount]);

	if (!hasData) {
		return (
			<div
				className="rounded-xl border border-dashed border-border bg-white p-6 text-sm text-muted-foreground"
				role="img"
				aria-label={`${ariaLabel} (no data)`}
			>
				No data for this range.
			</div>
		);
	}

	const xPos = (i: number) => (xCount <= 1 ? 50 : (i / (xCount - 1)) * 100);
	const yPos = (v: number) => 100 - ((v - min) / range) * 100;

	let peak = -Infinity;
	let peakLabel: string | null = null;
	let peakSeriesLabel: string | null = null;
	for (const s of series) {
		for (const point of s.data) {
			if (point.y === null) continue;
			if (point.y > peak) {
				peak = point.y;
				peakLabel = point.x;
				peakSeriesLabel = s.label;
			}
		}
	}

	const summary = `${ariaLabel}. Range ${xRef[0] ?? ""} to ${
		xRef[xCount - 1] ?? ""
	}. Peak ${peak === -Infinity ? "n/a" : peak.toLocaleString()} on ${
		peakLabel ?? "n/a"
	}${peakSeriesLabel && series.length > 1 ? ` (${peakSeriesLabel})` : ""}.`;

	return (
		<figure className="w-full" aria-labelledby={`${id}-title`}>
			<span id={`${id}-title`} className="sr-only">
				{summary}
			</span>

			{yLabel ? (
				<p className="text-xs text-muted-foreground mb-1">{yLabel}</p>
			) : null}

			<div className="flex">
				{/* Y-axis tick labels */}
				<div
					className="flex flex-col justify-between pr-2 text-right shrink-0"
					style={{ height }}
					aria-hidden="true"
				>
					{yTicks.map((t) => (
						<span
							key={t.frac}
							className="text-[10px] leading-none text-muted-foreground tabular-nums"
						>
							{formatTickValue(t.value)}
						</span>
					))}
				</div>

				{/* Chart area */}
				<div className="flex-1 min-w-0">
					<svg
						viewBox="0 0 100 100"
						preserveAspectRatio="none"
						className="w-full"
						style={{ height }}
						role="img"
						aria-label={ariaLabel}
					>
						{gridLines.map((g) => (
							<line
								key={g}
								x1="0"
								x2="100"
								y1={g * 100}
								y2={g * 100}
								stroke="currentColor"
								strokeWidth="0.2"
								className="text-zinc-200"
								vectorEffect="non-scaling-stroke"
							/>
						))}
						{series.map((s, idx) => {
							const points = s.data
								.map((d, i) =>
									d.y === null ? null : `${xPos(i)},${yPos(d.y)}`
								)
								.filter((p): p is string => p !== null)
								.join(" ");
							const dashArray = s.faded
								? "4 3"
								: idx === 0
									? "0"
									: idx === 1
										? "6 3"
										: idx === 2
											? "2 3"
											: "8 2 2 2";
							return (
								<polyline
									key={s.label}
									points={points}
									fill="none"
									stroke={s.color}
									strokeOpacity={s.faded ? 0.55 : 1}
									strokeWidth={s.faded ? 1.2 : 1.8}
									strokeDasharray={dashArray}
									strokeLinejoin="round"
									strokeLinecap="round"
									vectorEffect="non-scaling-stroke"
								/>
							);
						})}
					</svg>

					{/* X-axis date labels */}
					{xCount > 0 ? (
						<div className="relative h-5 mt-0.5" aria-hidden="true">
							{xTickIndices.map((i) => (
								<span
									key={i}
									className="absolute text-[10px] leading-none text-muted-foreground tabular-nums -translate-x-1/2"
									style={{ left: `${xPos(i)}%` }}
								>
									{formatXLabel(xRef[i])}
								</span>
							))}
						</div>
					) : null}
				</div>
			</div>

			<div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
				{series.map((s, idx) => {
					const dashArray = s.faded
						? "4 3"
						: idx === 0
							? "0"
							: idx === 1
								? "6 3"
								: idx === 2
									? "2 3"
									: "8 2 2 2";
					return (
						<span key={s.label} className="inline-flex items-center gap-1">
							<svg width="20" height="6" viewBox="0 0 20 6" aria-hidden="true">
								<line
									x1="0"
									y1="3"
									x2="20"
									y2="3"
									stroke={s.color}
									strokeWidth="2"
									strokeDasharray={dashArray}
									strokeOpacity={s.faded ? 0.55 : 1}
								/>
							</svg>
							{s.label}
						</span>
					);
				})}
			</div>
		</figure>
	);
};

export default LineChart;
